import { useCallback, useEffect, useState } from "preact/hooks";

import {
  ApiError,
  createAnimal as createAnimalApi,
  deleteAnimalWeight,
  deleteAttachment,
  fetchAnimalAttachments,
  fetchAnimals,
  fetchAnimalWeights,
  fetchHealthEvents,
  logAnimalWeight,
  logHealthEvent,
  patchAnimal,
  patchHealthEvent,
  setAnimalPrimaryPhoto,
  uploadAttachment,
  type CreateAnimalBody,
  type LogAnimalWeightBody,
  type LogHealthEventBody,
  type PatchAnimalBody,
  type PatchHealthEventBody,
} from "../lib/api";
import {
  PHOTO_BAD_TYPE_ERROR,
  PHOTO_CLIENT_MAX_BYTES,
  PHOTO_TOO_BIG_ERROR,
} from "../lib/recordsCopy";
import type { AnimalOut, AnimalWeightOut, AttachmentOut, HealthEventOut } from "../types";

/**
 * Sprint-005 C4 — the More tab's Animals list. `GET /animals` is open to
 * every authed role (admin/manager/member/api — `app/api/v1/animals.py`),
 * so `enabled` here is really just "the More > Animals screen is on
 * screen," never a role gate (contrast `useAdminUsers`/`useAdminCadences`,
 * whose `enabled` doubles as an admin-only gate).
 */
export function useAnimals(enabled: boolean) {
  const [animals, setAnimals] = useState<AnimalOut[] | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const rows = await fetchAnimals();
      setAnimals(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the animals list.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sprint-010 C4 (design-phase-c.md §2.3, ADR-002 A6 decision 7.1/7.4) —
  // originally the Overview tab's inline notes-only edit. Patches the ONE
  // changed row's `notes`/`updated_at` directly into local state — no
  // `refresh()` call, same §8 TD-010-class rule every other C3 write hook
  // already follows (`useAnimalWeights`/`useAnimalPhotos` above): a write
  // must never re-trigger `loading`, so the Animal detail overlay (already
  // mounted, reading this same array via `.find()`) never remounts/flashes.
  //
  // Sprint-016 F1 AN-01 — the inline textarea this fed is retired; `notes`
  // is now saved through `updateAnimalFields` below (the merged
  // `AnimalFormSheet` edit sheet submits the full field set, notes
  // included). No production caller invokes this function anymore — kept
  // as a working, correct capability rather than deleted, since removing
  // it buys nothing beyond this one file and isn't required by the
  // approved redesign direction.
  const patchNotes = useCallback(async (animalId: string, notes: string | null) => {
    const updated = await patchAnimal(animalId, { idempotency_key: crypto.randomUUID(), notes });
    setAnimals((prev) =>
      prev
        ? prev.map((a) =>
            a.id === animalId ? { ...a, notes: updated.notes, updated_at: updated.updated_at } : a,
          )
        : prev,
    );
  }, []);

  // Sprint-013 B2 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
  // Decision 4.4) — the Photos tab's "Set as card photo"/"Remove as card
  // photo" affordance, threaded through `AnimalDetailOverlay`'s
  // `onSetPrimaryPhoto` prop the SAME in-place-patch way `patchNotes`
  // above works: `primary_photo_attachment_id` is a field on
  // `AnimalOut` itself (not a separate tab-scoped resource like weights/
  // photos), so it belongs on THIS roster-wide hook, patched in place
  // (§8's TD-010-class rule) so both the open detail overlay and the
  // grid behind it (`AnimalsView`'s `AnimalCard`s, which derive their
  // `photoUrl` from this same field) reflect the change with no refetch.
  const setPrimaryPhoto = useCallback(async (animalId: string, attachmentId: string | null) => {
    const updated = await setAnimalPrimaryPhoto(animalId, {
      idempotency_key: crypto.randomUUID(),
      attachment_id: attachmentId,
    });
    setAnimals((prev) =>
      prev
        ? prev.map((a) =>
            a.id === animalId
              ? {
                  ...a,
                  primary_photo_attachment_id: updated.primary_photo_attachment_id,
                  updated_at: updated.updated_at,
                }
              : a,
          )
        : prev,
    );
  }, []);

  // Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5) — the "+ Add"
  // sheet's submit. Appends the created row directly into local state (the
  // same §8 TD-010-class rule as `patchNotes`/`setPrimaryPhoto` above) —
  // no `refresh()` call, so the grid never remounts/flashes on add.
  const createAnimal = useCallback(async (input: Omit<CreateAnimalBody, "idempotency_key">) => {
    const created = await createAnimalApi({ idempotency_key: crypto.randomUUID(), ...input });
    setAnimals((prev) => (prev ? [...prev, created] : [created]));
    return created;
  }, []);

  // Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5) — the Edit
  // sheet's submit (`AnimalDetailOverlay`'s admin-only "Edit" affordance):
  // name/species/birth_date/paddock, presence-based (a field omitted from
  // `updates` is left unchanged server-side — `PatchAnimalBody`'s own
  // contract). Same in-place-patch pattern as `patchNotes` above, kept as
  // a separate function since its caller (the form sheet) always supplies
  // the full field set, never just `notes`.
  //
  // Sprint-016 F1 AN-01 (ui-redesign-direction-2026-07-11.md item 1) widens
  // this to also accept `notes` — the merged `AnimalFormSheet` edit sheet
  // (`mode="edit"`) now submits every Overview-scope field, notes
  // included, through this ONE function (retiring the separate
  // `patchNotes`-via-inline-textarea path).
  const updateAnimalFields = useCallback(
    async (
      animalId: string,
      updates: Pick<PatchAnimalBody, "name" | "species" | "birth_date" | "paddock" | "notes">,
    ) => {
      const updated = await patchAnimal(animalId, {
        idempotency_key: crypto.randomUUID(),
        ...updates,
      });
      setAnimals((prev) => (prev ? prev.map((a) => (a.id === animalId ? updated : a)) : prev));
      return updated;
    },
    [],
  );

  // Sprint-013 C1 (design-spec-animal-cards-2026-07-10.md §5, ux-ruling-
  // inactive-grid-2026-07-10.md — E1 PM ruling) — the Archive flow's
  // `active: false` PATCH. No dedicated archive endpoint exists for
  // animals (SA ruling Decision 1.5 — `active` stays the pre-existing
  // terminal state). Patches the roster in place exactly like every other
  // write here — the caller (`AnimalsView`) re-derives its Active/All
  // filtered view from this same array, so the grid reflects the archive
  // with no refetch and no forced toggle-state change (E1 ruling item 5).
  const archiveAnimal = useCallback(async (animalId: string) => {
    const updated = await patchAnimal(animalId, {
      idempotency_key: crypto.randomUUID(),
      active: false,
    });
    setAnimals((prev) => (prev ? prev.map((a) => (a.id === animalId ? updated : a)) : prev));
    return updated;
  }, []);

  // Sprint-016 F1 AN-02 (ux-audit-whole-app-2026-07-11.md — S1, PM
  // fix-batch #1) — the Restore flow's `active: true` PATCH, mirroring
  // `archiveAnimal` above field-for-field (same endpoint, opposite
  // direction — `app/api/v1/animals.py`'s `PATCH` handler applies `active`
  // from either boolean with no directional restriction, confirmed
  // against the route's own `if "active" in fields_set and body.active is
  // not None` gate). No dedicated restore endpoint exists for animals,
  // same posture as `archiveAnimal`'s own docstring (SA ruling Decision
  // 1.5 — `active` stays the pre-existing terminal state, both
  // directions). Patches the roster in place — the caller (`AnimalsView`)
  // re-derives its Active/All filtered view from this same array.
  const restoreAnimal = useCallback(async (animalId: string) => {
    const updated = await patchAnimal(animalId, {
      idempotency_key: crypto.randomUUID(),
      active: true,
    });
    setAnimals((prev) => (prev ? prev.map((a) => (a.id === animalId ? updated : a)) : prev));
    return updated;
  }, []);

  return {
    animals,
    loading,
    error,
    refresh,
    patchNotes,
    setPrimaryPhoto,
    createAnimal,
    updateAnimalFields,
    archiveAnimal,
    restoreAnimal,
  };
}

/**
 * One animal's care/health timeline + the (admin-only) "Add a note"
 * mutation. Kept separate from `useAnimals` above so opening a detail
 * screen only fetches this one animal's history, not re-fetching the
 * whole roster.
 */
export function useAnimalHealth(animalId: string | null) {
  const [events, setEvents] = useState<HealthEventOut[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!animalId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchHealthEvents(animalId);
      setEvents(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the care history.");
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => {
    setEvents(null);
    void refresh();
  }, [refresh]);

  const addNote = useCallback(
    async (body: Omit<LogHealthEventBody, "idempotency_key">) => {
      if (!animalId) return;
      await logHealthEvent(animalId, { ...body, idempotency_key: crypto.randomUUID() });
      await refresh();
    },
    [animalId, refresh],
  );

  // Sprint-010 C4 (design-phase-c.md §2.4, ADR-002 A6 decision 7.1/7.4) —
  // the Care & health tab's per-item edit sheet. Patches the ONE edited
  // event directly into local state (§8's TD-010-class rule) — the rest
  // of the timeline, and whichever tab is active, never remounts.
  const editNote = useCallback(
    async (eventId: string, body: Omit<PatchHealthEventBody, "idempotency_key">) => {
      if (!animalId) return;
      const updated = await patchHealthEvent(animalId, eventId, {
        ...body,
        idempotency_key: crypto.randomUUID(),
      });
      setEvents((prev) => (prev ? prev.map((e) => (e.id === eventId ? updated : e)) : prev));
    },
    [animalId],
  );

  return { events, loading, error, refresh, addNote, editNote };
}

/** Newest-`weighed_on`-first, `created_at` as a tiebreak for same-day
 * entries (matches `GET /weights`'s own server-side ordering — see
 * `lib/api.ts::fetchAnimalWeights`'s docstring). Re-applied locally after
 * every add/delete so the sparkline/caption/list stay correctly ordered
 * WITHOUT a network refetch — the TD-010-class rule (design-phase-c.md
 * §8) this hook is built around: a write only ever patches local state
 * in place, it never re-triggers `loading`, so the Weights tab can never
 * remount/flash on save. */
function sortWeightsNewestFirst(rows: AnimalWeightOut[]): AnimalWeightOut[] {
  return [...rows].sort((a, b) => {
    if (a.weighed_on !== b.weighed_on) return b.weighed_on.localeCompare(a.weighed_on);
    return b.created_at.localeCompare(a.created_at);
  });
}

/**
 * Sprint-010 C3 (ADR-002 A6 decision 6) — the Weights tab's data + the
 * two admin/api-only mutations. Reads are all-roles server-side
 * (`GET /animals/{id}/weights`); this hook itself applies no role gate —
 * the caller (`AnimalDetailOverlay`'s `isAdmin` prop) decides whether to
 * render "+ Log a weight"/"Remove" at all, matching every other
 * write-affordance gate in this app (the 403 the endpoint would return is
 * the real enforcement boundary, same posture as `useAnimalHealth.addNote`
 * above).
 */
export function useAnimalWeights(animalId: string | null) {
  const [weights, setWeights] = useState<AnimalWeightOut[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!animalId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAnimalWeights(animalId);
      setWeights(sortWeightsNewestFirst(rows));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the weight history.");
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => {
    setWeights(null);
    void refresh();
  }, [refresh]);

  // No `refresh()` call on success — the new/removed row is patched
  // directly into local state (§8's "keep the content component mounted
  // continuously" rule applied in its strongest form: not even a
  // busy-overlay refetch is needed when the mutation's own response
  // already carries everything the list needs to stay correct).
  const addWeight = useCallback(
    async (body: Omit<LogAnimalWeightBody, "idempotency_key">) => {
      if (!animalId) return;
      const created = await logAnimalWeight(animalId, {
        ...body,
        idempotency_key: crypto.randomUUID(),
      });
      setWeights((prev) => sortWeightsNewestFirst([...(prev ?? []), created]));
    },
    [animalId],
  );

  const removeWeight = useCallback(
    async (weightId: string) => {
      if (!animalId) return;
      await deleteAnimalWeight(animalId, weightId, crypto.randomUUID());
      setWeights((prev) => (prev ? prev.filter((w) => w.id !== weightId) : prev));
    },
    [animalId],
  );

  return { weights, loading, error, refresh, addWeight, removeWeight };
}

// ---------------------------------------------------------------------------
// Sprint-010 C3 (ADR-002 A6 decisions 2-4) — the Photos tab. Reads are
// all-roles; upload/delete are admin/api only. Every in-flight upload is
// tracked as its own local "tile" (design-phase-c.md §2.6/§8 — "a
// placeholder tile is appended to the START of the grid immediately on
// selection ... own grid cell"), entirely separate from the fetched
// `photos` list, so an upload/retry/failure never touches — let alone
// remounts — the rest of the grid.
// ---------------------------------------------------------------------------

export type PhotoUploadStatus = "uploading" | "failed";

export interface PhotoUploadTile {
  localId: string;
  file: File;
  status: PhotoUploadStatus;
  /** Set only when `status === "failed"` — the exact copy to show in
   * this tile (§6's client-reject/server-reject/generic-retry strings). */
  errorMessage: string | null;
}

/** §2.6's client-side pre-check — "UX-only, never the security boundary."
 * Returns the specific rejection copy, or `null` if the file passes this
 * cheap local check (a genuine server-side rejection the client missed
 * is handled separately, at the `catch` site below). */
function clientPrecheckPhoto(file: File): string | null {
  if (!file.type.startsWith("image/")) return PHOTO_BAD_TYPE_ERROR;
  if (file.size > PHOTO_CLIENT_MAX_BYTES) return PHOTO_TOO_BIG_ERROR;
  return null;
}

export function useAnimalPhotos(animalId: string | null) {
  const [photos, setPhotos] = useState<AttachmentOut[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<PhotoUploadTile[]>([]);

  const refresh = useCallback(async () => {
    if (!animalId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAnimalAttachments(animalId);
      setPhotos(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the photos.");
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => {
    setPhotos(null);
    void refresh();
  }, [refresh]);

  const runUpload = useCallback(
    async (localId: string, file: File) => {
      if (!animalId) return;
      const precheckError = clientPrecheckPhoto(file);
      if (precheckError) {
        setUploads((prev) =>
          prev.map((t) =>
            t.localId === localId ? { ...t, status: "failed", errorMessage: precheckError } : t,
          ),
        );
        return;
      }
      try {
        const created = await uploadAttachment(animalId, file, crypto.randomUUID());
        // Success — drop the tile, prepend the real thumbnail in its
        // place (§2.6: "replaced in place ... the rest of the grid must
        // not remount"; `GET .../attachments` is itself newest-first, so
        // prepending locally matches server ordering without a refetch).
        setUploads((prev) => prev.filter((t) => t.localId !== localId));
        setPhotos((prev) => [created, ...(prev ?? [])]);
      } catch (err) {
        // §2.6 — the specific "too big"/"doesn't look like a photo" copy
        // is reserved for the CLIENT-caught case above, caught before any
        // network request. A 413/415 that reaches here means the client
        // precheck missed it (a genuine server-side rejection) — shown
        // with the generic "couldn't be saved" copy, never the specific
        // strings, per §6's own distinction between those two rows. Any
        // OTHER failure (network error, 5xx) gets the tile's default
        // retry copy.
        const message =
          err instanceof ApiError && (err.status === 413 || err.status === 415)
            ? "That photo couldn't be saved — try a different one."
            : "Couldn't upload — tap to try again.";
        setUploads((prev) =>
          prev.map((t) =>
            t.localId === localId ? { ...t, status: "failed", errorMessage: message } : t,
          ),
        );
      }
    },
    [animalId],
  );

  const startUpload = useCallback(
    (file: File) => {
      const localId = crypto.randomUUID();
      setUploads((prev) => [{ localId, file, status: "uploading", errorMessage: null }, ...prev]);
      void runUpload(localId, file);
    },
    [runUpload],
  );

  const retryUpload = useCallback(
    (localId: string) => {
      const tile = uploads.find((t) => t.localId === localId);
      if (!tile) return;
      setUploads((prev) =>
        prev.map((t) =>
          t.localId === localId ? { ...t, status: "uploading", errorMessage: null } : t,
        ),
      );
      void runUpload(localId, tile.file);
    },
    [uploads, runUpload],
  );

  const removePhoto = useCallback(async (attachmentId: string) => {
    await deleteAttachment(attachmentId, crypto.randomUUID());
    setPhotos((prev) => (prev ? prev.filter((p) => p.id !== attachmentId) : prev));
  }, []);

  return { photos, loading, error, refresh, uploads, startUpload, retryUpload, removePhoto };
}
